import { getAccounts, getFxRates } from '@/mocks/api';
import { useAsync } from '@/hooks/useAsync';
import { resolveContext } from '@/state/session';
import { MobileHeader } from '@/components/mobile-shell/MobileHeader';
import { Amount, ListRow, SectionHeader } from '@/components/banking';
import { CardSkeleton, ErrorState, LoadingRegion, Notice } from '@/components/feedback';
import { formatDateTime } from '@/services/formatters';

/**
 * Exchange and FX rates.
 *
 * TRACE:TT-R106-01 — market rates live here rather than cluttering home.
 *
 * Rates are labelled indicative and timestamped. Executing a conversion needs a
 * dealable quote from the provider, which is not confirmed in this package, so
 * the action is presented as a handoff rather than a button that pretends to
 * trade.
 */
export function FxPage() {
  const resolved = resolveContext();
  const companyId = resolved?.company.id ?? null;
  const userId = resolved?.user.id ?? null;
  const base = resolved?.company.baseCurrency ?? 'GBP';

  const rates = useAsync(async () => (await getFxRates(base)).data, [base]);
  const accounts = useAsync(async () => {
    if (!companyId || !userId) return [];
    return (await getAccounts(companyId, userId)).data;
  }, [companyId, userId]);

  if (!resolved) return null;

  return (
    <>
      <MobileHeader title="Exchange" backTo="/mobile/more" backLabel="More" />
      <div className="tm-stack">
        <section aria-labelledby="fx-rates">
          <SectionHeader title="Indicative rates" id="fx-rates" />
          {rates.status === 'loading' ? (
            <LoadingRegion label="Loading rates">
              <CardSkeleton height={140} />
            </LoadingRegion>
          ) : rates.status === 'error' ? (
            <ErrorState error={rates.error} onRetry={rates.reload} />
          ) : (
            <div className="tm-card tm-card--flush">
              <ul className="tm-rows">
                {rates.data.map((r) => (
                  <li key={r.pair}>
                    <ListRow
                      title={r.pair}
                      meta={`As at ${formatDateTime(r.asOf)}`}
                      end={<span className="tm-amount">{r.rate.toFixed(4)}</span>}
                      ariaLabel={`${r.from} to ${r.to}, indicative rate ${r.rate.toFixed(4)}`}
                    />
                  </li>
                ))}
              </ul>
            </div>
          )}
          <p className="tm-hint" style={{ marginBlockStart: 8 }}>
            Indicative only. The rate you receive is confirmed when a conversion is quoted.
          </p>
        </section>

        <section aria-labelledby="fx-balances">
          <SectionHeader title="Your currency balances" id="fx-balances" />
          <div className="tm-card tm-card--flush">
            <ul className="tm-rows">
              {(accounts.data ?? []).map((a) => (
                <li key={a.id}>
                  <ListRow
                    to={`/mobile/accounts/${a.id}`}
                    title={a.displayName}
                    meta={a.currency}
                    end={<Amount minorUnits={a.availableBalance} currency={a.currency} />}
                  />
                </li>
              ))}
            </ul>
          </div>
        </section>

        <Notice tone="info" title="Converting currency">
          Placing a conversion needs a live dealable quote from the provider. That integration is
          not confirmed in this package, so conversions are completed on the web app where the
          quote and settlement terms are shown in full.
        </Notice>
      </div>
    </>
  );
}
